import { createRoute, createRootRoute, createRouter, redirect } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { UniversalAppShell } from "@tennr/frontend/components/App";
import { HomePage } from "@tennr/frontend/pages/HomePage";
import { ProductPage } from "@tennr/frontend/pages/ProductPage";
import { AboutPage } from "@tennr/frontend/pages/AboutPage";
import { BookPage } from "@tennr/frontend/pages/BookPage";
import { CareersPage } from "@tennr/frontend/pages/CareersPage";
import { FaxItPage } from "@tennr/frontend/pages/FaxItPage";
import { EventsPage } from "@tennr/frontend/pages/EventsPage";
import { ResourcesPage } from "@tennr/frontend/pages/ResourcesPage";
import { PressPage } from "@tennr/frontend/pages/PressPage";
import { CompetitorComparisonPage } from "@tennr/frontend/pages/CompetitorComparisonPage";
import { CaseStudiesPage } from "@tennr/frontend/pages/CaseStudiesPage";
import { CaseStudyDetailPage } from "@tennr/frontend/pages/CaseStudyDetailPage";
import { NotFoundPage, ClientErrorPage } from "@tennr/frontend/pages/ErrorPage";
import { CSSBackground } from "@tennr/frontend/components/WebGLBackground/CSSBackground";
import { Outlet } from "@tanstack/react-router";
import { getAllCaseStudies, getCaseStudyBySlug } from "@tennr/frontend/lib/sanity/client";
import { PrivacyPage } from "@tennr/frontend/pages/PrivacyPage";
import { AmbassadorPage } from "../pages/AmbassadorPage";

// create the root route
const rootRoute = createRootRoute();

// create an app layout route that includes the universal shell
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  component: () => (
    <UniversalAppShell>
      <Outlet />
    </UniversalAppShell>
  ),
});

// define the index route (homepage)
const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/",
  component: HomePage,
});

// define the product route
const productRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/product",
  component: ProductPage,
});

// define the about route
const aboutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/about",
  component: AboutPage,
});

const oldAboutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/about-tennr",
  loader: () =>
    redirect({
      to: "/about",
    }),
});

// define the book route
const bookRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/book-a-demo",
  component: BookPage,
});

//redirect /book
const oldBookRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/book",
  loader: () =>
    redirect({
      to: "/book-a-demo",
    }),
});

// Note: /demo, /demo-*, and /demo/* and webinar/ambassador equivalents are now handled by the catch-all route below

const existingCompetitors = [
  "abbadox",
  "alayacare",
  "alpha-nodus",
  "calvient",
  "celeritas-ai",
  "change-healthcare",
  "darby",
  "dmescripts",
  "forcura",
  "lamar-health",
  "latent-health",
  "mandolin",
  "medsender",
  "notable-systems",
  "parachute-health",
  "samacare",
  "silna",
  "synthpop",
];

const competitorComparisonRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/lp/$competitorId",
  loader: async ({ params }) => {
    const competitorId = params.competitorId;
    if (existingCompetitors.includes(competitorId) == false) {
      throw redirect({
        to: "/",
      });
    }
    return { competitorId };
  },
  component: CompetitorComparisonPage,
});

// define the case study detail route
const caseStudyDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/customer-stories/$caseStudyId",
  loader: async ({ params }) => {
    const caseStudy = await getCaseStudyBySlug(params.caseStudyId);
    if (!caseStudy) {
      // Redirect to a non-existent route to trigger the 404 page
      throw redirect({
        to: "/not-found",
      });
    }
    return { caseStudy };
  },
  component: CaseStudyDetailPage,
});

// define the case studies route
const caseStudiesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/customer-stories",
  loader: async () => {
    const caseStudies = await getAllCaseStudies();
    return { caseStudies };
  },
  component: CaseStudiesPage,
});

// redirect /studies to /customer-stories
const oldStudiesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/studies",
  loader: () =>
    redirect({
      to: "/customer-stories",
    }),
});

// redirect /case-study to /customer-stories
const oldCaseStudyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/case-study",
  loader: () =>
    redirect({
      to: "/customer-stories",
    }),
});

// redirect /studies/* to /customer-stories/*
const studiesWildcardRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/studies/$",
  loader: ({ params }) =>
    redirect({
      to: `/customer-stories/${params._splat}`,
    }),
});

// redirect /case-study/* to /customer-stories/*
const caseStudyWildcardRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/case-study/$",
  loader: ({ params }) =>
    redirect({
      to: `/customer-stories/${params._splat}`,
    }),
});

const privacyPolicyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/privacy-policy",
  component: PrivacyPage,
});

const careersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/careers",
  component: CareersPage,
});

const resourcesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/resources",
  component: ResourcesPage,
});

const eventsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/events",
  component: EventsPage,
});


const faxItRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/fax-it",
  component: FaxItPage,
});

const pressRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/press",
  component: PressPage,
});
const ambassadorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/ambassador",
  component: AmbassadorPage,
});
const oldPrivacyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/privacy",
  loader: () =>
    redirect({
      to: "/privacy-policy",
    }),
});

// jobs route - redirects to external Ashby page
const jobsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/jobs",
  loader: () =>
    redirect({
      href: "https://jobs.ashbyhq.com/tennr",
    }),
});

// Studio route - isolated from app shell
const StudioPage = lazy(() =>
  import("@tennr/frontend/pages/StudioPage").then((m) => ({
    default: m.StudioPage,
  }))
);

const studioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio/$",
  component: () => (
    <Suspense
      fallback={
        <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          Loading Studio...
        </div>
      }
    >
      <StudioPage />
    </Suspense>
  ),
});

// redirect a bunch of old pages
const homeRedirectRoutes = [
  "/all-resources",
  "/all-resources/$",
  "/nica-webinar",
  "/partner-registration",
  "/parnter-registration",
  "/resource",
  "/rte",
  "/test-page",
  "/the-q1-2025-tennr-refresh",
  "/the-q4-2024-tennr-refresh",
  "/webinars---tennr",
  "/webinars/$",
];

// Catch-all route for pattern matching redirects and 404s
const catchAllRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "$",
  loader: ({ params }) => {
    const path = params._splat;

    // Handle ambassador*, demo*, webinar* patterns
    if (path?.startsWith("ambassador")) {
      return redirect({ to: "/" });
    }
    if (path?.startsWith("demo")) {
      return redirect({ to: "/book-a-demo" });
    }
    if (path?.startsWith("webinar")) {
      return redirect({ to: "/" });
    }

    // For all other unmatched routes, return null to render 404 component
    return null;
  },
  component: NotFoundPage,
});

// create redirect routes for all home redirects
const homeRedirectRoutesCreated = homeRedirectRoutes.map((path) =>
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path,
    loader: () =>
      redirect({
        to: "/",
      }),
  })
);

// create the route tree
const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    indexRoute,
    productRoute,
    aboutRoute,
    oldAboutRoute,
    bookRoute,
    competitorComparisonRoute,
    caseStudiesRoute,
    caseStudyDetailRoute,
    jobsRoute,
    privacyPolicyRoute,
    oldPrivacyRoute,
    oldStudiesRoute,
    oldCaseStudyRoute,
    studiesWildcardRedirectRoute,
    caseStudyWildcardRedirectRoute,
    oldBookRoute,
    careersRoute,
    pressRoute,
    resourcesRoute,
    eventsRoute,
    faxItRoute,
    ambassadorRoute,
    ...homeRedirectRoutesCreated,
    catchAllRedirectRoute, // Must be last to catch unmatched routes
  ]),
  studioRoute,
]);

// create and export the router
export const router = createRouter({
  routeTree,
  defaultPreload: "intent", // Preload on hover/focus
  defaultPreloadDelay: 50, // Quick preload
  scrollRestoration: true,
  defaultErrorComponent: ({ error }) => (
    <UniversalAppShell>
      <ClientErrorPage error={error} />
    </UniversalAppShell>
  ),
});

// type safety for routes
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
